import { useEffect, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import { AiFillCaretRight } from "react-icons/ai";
import { BiCheck, BiPlus, BiMinus } from "react-icons/bi";
import API from "../../../utils/API";
import AlertModal from "../../../utils/components/AlertModal";
import SelectBox from "../../../utils/components/SelectBox";

const SubTreeItem = ({
  item,
  handleUpdateSingleItem,
  parentId = 0,
  translate,
  handleRemoveSubSet,
  setType,
  roles,
  setNewSubSetItem,
  level,
}) => {
  const [subSet, setSubSet] = useState(item);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubTreeOpen, setIsSubTreeOpen] = useState(false);
  const [subTreeId] = useState(
    `sub-tree-${parentId ? `${parentId}-${item.id}` : `${item.id}`}`
  );
  const [newName, setNewName] = useState(item.name);

  const [details, setDetails] = useState([]);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);

  useEffect(() => {
    const subTreeEl = document.querySelector(`#${subTreeId} .sub-tree`);

    if (subTreeEl) {
      subTreeEl.setAttribute("data-max-height", subTreeEl.scrollHeight);
    }
  }, [subTreeId]);

  useEffect(() => {
    if (isSubTreeOpen && details.length === 0 && subSet.hasDetail) {
      setIsDetailsLoading(true);
      API.getData(`${setType?.subSetUrl}/${item.id}`).then(({ data: res }) => {
        setDetails(
          res.data.details.map((detail) => ({
            id: detail.id,
            name: detail.name,
            hasDetail: detail.is_details,
            set_id: item.set_id,
            parent_id: item.id,
          }))
        );
        setIsDetailsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubTreeOpen, details, subSet]);

  const handleUpdateName = () => {
    if (newName.length > 0 && newName !== item.name) {
      API.putData(`${setType?.subSetUrl}/${item.id}`, { name: newName })
        .then(() => {
          setSubSet({ ...subSet, name: newName });
          setIsEditing(false);
        })
        .catch(() => {
          setIsEditing(false);
        });
    } else setIsEditing(false);
  };

  const handleAddNew = (data) => {
    const newDetails = [
      ...details,
      {
        id: data.id,
        name: data.name,
        hasDetail: false,
        set_id: item.set_id,
        parent_id: data.parent_id,
      },
    ];

    setDetails(newDetails);
    if (!subSet.hasDetail) setSubSet({ ...subSet, hasDetail: true });
  };

  const onDelete = (id) => {
    if (!id) return false;

    confirmAlert({
      customUI: ({ onClose }) => (
        <AlertModal
          buttons={[
            {
              label: translate("cancel"),
              onClick: onClose,
              classNames: "btn btn-secondary",
            },
            {
              label: translate("delete"),
              onClick: () => {
                handleRemove(id, onClose);
              },
              classNames: "btn btn-danger",
            },
          ]}
          message={translate("delete_confirmation")}
        />
      ),
    });
  };

  const handleRemove = (id, closeModal) => {
    API.deleteData(`${setType?.subSetUrl}/${id}`).then(() => {
      const newDetails = [...details];
      const index = newDetails.findIndex((item) => item.id === id);

      if (index > -1) {
        newDetails.splice(index, 1);
        if (newDetails.length === 0) setSubSet({ ...subSet, hasDetail: false });
        setDetails(newDetails);
      }

      closeModal();
    });
  };

  const handleAddRole = (values, actionType) => {
    const { option, removedValue } = actionType;

    if (option) {
      API.postData(`status_set_detail/${item.id}/project_role/${option.id}`);

      const newItem = { ...item };

      newItem.roles = [...newItem.roles, { id: option.id, name: option.label }];

      handleUpdateSingleItem(newItem);
    } else if (removedValue) {
      API.deleteData(
        `status_set_detail/${item.id}/project_role/${removedValue.id}`
      );

      const newItem = { ...item };

      newItem.roles = newItem.roles.filter(
        (item) => item.id !== removedValue.id
      );

      handleUpdateSingleItem(newItem);
    }
  };

  return (
    <div
      className="set-item align-items-center"
      id={subTreeId}
      data-has-select={Boolean(roles?.length)}
      data-is-open={isSubTreeOpen}
      data-level={level}
    >
      {subSet?.hasDetail ? (
        <label
          className="show-more-button d-flex justify-content-center align-items-center"
          data-is-active={isSubTreeOpen}
        >
          <AiFillCaretRight />
          <input
            type="checkbox"
            className="show-more-checkbox"
            onChange={(e) => setIsSubTreeOpen(e.target.checked)}
          />
        </label>
      ) : (
        <span>&nbsp;</span>
      )}

      <span className="text w-100 h-100 d-flex align-items-center">
        <p
          className={`py-2 m-0 w-100 ${isEditing ? `d-none` : `d-flex`}${
            subSet?.hasDetail ? ` cursor--pointer` : ` cursor--default`
          }`}
          onDoubleClick={(e) => {
            e.preventDefault();
            setIsEditing(item.id);
          }}
          onClick={(e) => {
            if (subSet?.hasDetail && !isEditing)
              setIsSubTreeOpen(!isSubTreeOpen);
          }}
        >
          {newName}
        </p>
        <input
          type="text"
          name="name"
          className={`w-100 ${isEditing ? `d-flex` : `d-none`}`}
          value={newName}
          readOnly={isEditing !== item.id}
          onChange={(e) => setNewName(e.target.value)}
        />
      </span>
      <span className="options d-flex justify-content-around">
        {isEditing === item.id && (
          <span
            className="apply-button"
            onClick={handleUpdateName}
            role="button"
          >
            <BiCheck size="20px" />
          </span>
        )}
        {isEditing !== item.id && (
          <>
            {setType?.hasLayers && (
              <span
                className="add-button"
                role="button"
                onClick={() => setNewSubSetItem(item, handleAddNew)}
              >
                <BiPlus />
              </span>
            )}
            <span
              className="remove-button"
              role="button"
              onClick={() => handleRemoveSubSet(item.id)}
            >
              <BiMinus />
            </span>
          </>
        )}
      </span>
      {Boolean(roles?.length) && (
        <span className="roles">
          <SelectBox
            options={roles.map((role) => ({
              id: role.id,
              value: role.id,
              label: role.title,
            }))}
            isMulti={true}
            onChange={(value, actionType) => {
              handleAddRole(value, actionType);
            }}
            value={item?.roles?.map((role) => ({
              id: role.id,
              value: role.id,
              label: role.name,
            }))}
            placeholder={translate(
              `project_settings.${setType?.translateKey}.group_to_use`
            )}
          />
        </span>
      )}
      {isDetailsLoading && (
        <div className="sub-tree overflow-hidden" data-is-active={true}>
          {translate("loading")}
        </div>
      )}
      {Boolean(details.length) && (
        <div
          className="sub-tree overflow-hidden"
          data-is-active={isSubTreeOpen}
        >
          {details.map((detail) => (
            <SubTreeItem
              key={detail.id}
              item={detail}
              handleUpdateSingleItem={handleUpdateSingleItem}
              parentId={parentId ? `${parentId}-${detail.id}` : detail.id}
              translate={translate}
              handleRemoveSubSet={onDelete}
              setType={setType}
              setNewSubSetItem={setNewSubSetItem}
              level={level + 1 > 5 ? 1 : level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SubTreeItem;
